import { ConfigService } from './../config-service/config.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Stock } from '@stockcare-storage/stock/stock';
import { Observable } from 'rxjs';
import { AnalyticsNames } from '@stockcare-helpers/analytics-names';
import { PerformanceMetricService } from '@stockcare-services/performance-metric-service/performance-metric-service';
import { StockStorageService } from '@stockcare-storage/stock/stock-storage.service';
import { TheatreBasketItem } from '@stockcare-models/TheatreBasketItem';
import { TheatreBasketPack } from '@stockcare-models/TheatreBasketPack';
import { MaterialType } from '@stockcare-models/MaterialType';
import { QueueType } from '@stockcare-models/QueueType';
import { QueueService } from '@stockcare-services/queue-service/queue.service';
import { NoStockMaterialItem } from '@stockcare-models/NoStockMaterialItem';
import { IRequestStockTransfer } from '@stockcare-models/IRequestStockTransfer';
import { IMaterialTransfer } from '@stockcare-models/IMaterialTransfer';
import { IStockTransfer } from '@stockcare-models/IStockTransfer';
import { RejectAcceptTransfer } from '@stockcare-models/RejectAcceptTransfer';

@Injectable({
  providedIn: 'root',
})
export class StockService {
  constructor(
    private readonly http: HttpClient,
    private readonly conf: ConfigService,
    private readonly performanceMetricService: PerformanceMetricService,
    private readonly stockStorageService: StockStorageService,
    private queueService: QueueService,
  ) {}

  getMaterials(stocklocationId): Observable<Stock[]> {
    const startTime = new Date();
    return new Observable((observer) => {
      this.http
        .get<Stock[]>(`${this.conf.config.baseUrl}/stocklevel/${stocklocationId}`)
        .subscribe(
          (result) => {
            observer.next(result);
            this.performanceMetricService.buildAndSubmitMetrics(startTime, AnalyticsNames.materialListQuery);
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  submitNoStockForMaterial(noStockItem: NoStockMaterialItem): Observable<any> {
    return new Observable((observer) => {
      this.queueService.enqueue(QueueType.NoStockFoundForMaterialQueue, noStockItem).subscribe(
        () => {
          observer.next(true);
        },
        (error) => {
          observer.error(false);
        }
      );
    });
  }

  transferMaterial(material: IMaterialTransfer): Observable<any> {
    return new Observable((observer) => {
      this.http.post<IMaterialTransfer>(`${this.conf.config.baseUrl}/stock/transfer/`, material)
        .subscribe(
          (result) => {
            observer.next(result);
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  requestStockTransfer(stockTransfer: IRequestStockTransfer): Observable<any> {
    return new Observable((observer) => {
      this.http.post<IRequestStockTransfer>(`${this.conf.config.baseUrl}/stock/requestTransfer`, stockTransfer)
        .subscribe(
          (result) => {
            observer.next(result);
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  rejectStockTransfer(stockTransferId: number, userId: string): Observable<any> {
    var rejectTransfer = new RejectAcceptTransfer;
    rejectTransfer.stockTransferId = stockTransferId;
    rejectTransfer.userId = userId;
    return new Observable((observer) => {
      this.http.post<RejectAcceptTransfer>(`${this.conf.config.baseUrl}/stock/rejectStockTransfer`, rejectTransfer)
        .subscribe(
          (result) => {
            observer.next(result);
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  acceptStockTransfer(stockTransferId: number, userId: string): Observable<any> {
    var rejectTransfer = new RejectAcceptTransfer;
    rejectTransfer.stockTransferId = stockTransferId;
    rejectTransfer.userId = userId;
    return new Observable((observer) => {
      this.http.post<RejectAcceptTransfer>(`${this.conf.config.baseUrl}/stock/approveStockTransfer`, rejectTransfer)
        .subscribe(
          (result) => {
            observer.next(result);
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  getPendingStockTransfersForUser(userId: string): Observable<IStockTransfer[]> {
    return this.http.get<IStockTransfer[]>(`${this.conf.config.baseUrl}/stock/getAllPendingStockTransfersForUser/${userId}`);
  }

  getAllPendingStockTransfersAsAdmin(institutionId: number): Observable<IStockTransfer[]> {
    return this.http.get<IStockTransfer[]>(`${this.conf.config.baseUrl}/stock/getAllPendingStockTransfers/${institutionId}`);
  }

  async updateMaterialData(materials: TheatreBasketItem[], allPacks?: TheatreBasketPack[]) {
    materials
      .filter((x) => x.materialType === MaterialType.Materials)
      .forEach(async (material) => {
        const stockItem = await this.stockStorageService.getStockByIndex(material.barcode);
        if (stockItem) {
          var usedQuantity = material.originalUsedQuantity == 0 ? material.quantity : material.originalUsedQuantity;
          if (allPacks)
            allPacks
              .forEach((pack) => {
                pack.materials
                  .filter((x) => x.packQuantity > 0 && x.barcode === material.barcode)
                  .forEach((x) => {
                    usedQuantity += x.packQuantity;
                  });
              });

          material.availableStock = usedQuantity + stockItem.quantity;

          if (allPacks)
          allPacks.forEach((pack) => {
            pack.materials
              .filter((x) => x.packQuantity > 0)
              .forEach(async (x) => {
                const stockItem = await this.stockStorageService.getStockByIndex(x.barcode);
                var usedQuantity = 0;
                var usedBasketQuantity = 0;
    
                allPacks
                  .forEach((pack) => {
                    pack.materials
                      .filter((m) => m.packQuantity > 0 && m.barcode == x.barcode)
                      .forEach((pm) => {
                        materials.filter(y => y.barcode === pm.barcode)
                        .forEach(m => {
                          usedBasketQuantity += m.quantity
                        })
                        usedQuantity += pm.packQuantity;
                      });
                  });
    
                if (stockItem) {
                  if (material.barcode === stockItem .barcode) {
                    material.availableStock = stockItem.quantity + usedQuantity + usedBasketQuantity;
                  }

                  x.availableStock = stockItem.quantity + usedQuantity + usedBasketQuantity;
                }
              });
          });
        }
      });
  }
}
